import React from 'react'
import CustomSelect from '../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx'
import RubroSheetTabs from './RubroSheetTabs.tsx'
import RubroQuotationWindowSection from './RubroQuotationWindowSection.tsx'
import sectionStyles from './RubroPublishWorkSection.module.scss'
import type { DocumentationReviewContext } from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import type { MetricBudgetSyncPhase } from './projectAutofillCard/hooks/useRubroMetricBudgetSilentSync.ts'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  isRubroFallbackOtrosKind,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizeDocumentationSlotSources,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import cardStyles from './ProjectAutofillCard.module.scss'
import RubroDocumentationSectionIntro from './RubroDocumentationSectionIntro.tsx'
import { rubroSourceFileLabel } from './projectAutofillCard/projectAutofillCardUtils.ts'
import styles from './ProjectAutofillRubroDetailModal.module.scss'

const PUBLISHED_HINT =
  'Para editar rubros o datos de la obra publicada, usá «Ir a la obra» y gestioná todo desde la ficha.'

function isRubroFuzzyKind(kind?: string): boolean {
  return String(kind || '').toLowerCase().includes('fuzzy')
}

function isRubroUnresolvedKind(kind?: string): boolean {
  return String(kind || '').toLowerCase() === 'unresolved'
}

export type ProjectAutofillRubroDetailBodyProps = {
  bodyClassName?: string
  rubro: RubroRow
  tempId: string
  skillOptions: SkillOption[]
  draftLocked: boolean
  draftId: string | undefined
  isSaving: boolean
  uploadingSlotKey: string | null
  docPdfUrl: string
  docPageCount: number | null | undefined
  metadataFiles: DraftMetadataFileEntry[]
  today: string
  projectStartDate: string
  projectEndDate: string
  advisoryNotes: string[]
  handleRubroSkillSelect: (tempId: string, value: string, rubro: RubroRow) => void
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  handleSelectRubroSlotFileId: (
    tempId: string,
    slot: keyof DocumentationSlots,
    fileId: string | null
  ) => void
  handleRubroSlotPagesChange: (
    tempId: string,
    slot: keyof DocumentationSlots,
    pages: number[]
  ) => void
  handleRubroDocumentationChipRemove: (
    tempId: string,
    slot: keyof DocumentationSlots,
    page: number,
    fileId?: string
  ) => void
  handleUploadRubroSlotFile: (
    tempId: string,
    slot: keyof DocumentationSlots,
    file: File
  ) => void
  fuzzyConfirmByRubro: Record<string, boolean>
  setFuzzyConfirmByRubro: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  rubroUnresolvedNeedsPick: (sp: RubroRow) => boolean
  clampIsoDateToMax: (iso: string, maxIso: string) => string
  onOpenBudgetSheetFullscreen: (tempId: string) => void
  onOpenMetricSheetFullscreen: (tempId: string) => void
  onOpenTechnicalSpecFullscreen: (tempId: string) => void
  documentationReview: DocumentationReviewContext
  getMetricBudgetSyncPhase?: (tempId: string) => MetricBudgetSyncPhase
  draftAnalyzed?: boolean
  onImportMetricEdify?: (tempId: string) => void
  isMetricImporting?: (tempId: string) => boolean
  metricImportError?: (tempId: string) => string | null | undefined
}

const ProjectAutofillRubroDetailBody: React.FC<ProjectAutofillRubroDetailBodyProps> = ({
  bodyClassName,
  rubro: sp,
  tempId: rid,
  skillOptions,
  draftLocked,
  draftId,
  isSaving,
  uploadingSlotKey,
  docPdfUrl,
  docPageCount,
  metadataFiles,
  today,
  projectStartDate,
  projectEndDate,
  advisoryNotes,
  handleRubroSkillSelect,
  updateRubro,
  handleSelectRubroSlotFileId,
  handleRubroSlotPagesChange,
  handleRubroDocumentationChipRemove,
  handleUploadRubroSlotFile,
  fuzzyConfirmByRubro,
  setFuzzyConfirmByRubro,
  rubroUnresolvedNeedsPick,
  clampIsoDateToMax,
  onOpenBudgetSheetFullscreen,
  onOpenMetricSheetFullscreen,
  onOpenTechnicalSpecFullscreen,
  documentationReview,
  getMetricBudgetSyncPhase,
  draftAnalyzed,
  onImportMetricEdify,
  isMetricImporting,
  metricImportError
}) => {
  const slots = normalizeDocumentationSlots(sp.documentationSlots)
  const slotSrcNorm = normalizeDocumentationSlotSources(sp.documentationSlotSources ?? {})
  const idsNorm = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
  const rootClassName = [
    bodyClassName ?? styles.body,
    cardStyles.projectAutofillSubprojectBody,
    cardStyles.projectAutofillRubroSurface
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={rootClassName}>
      {isRubroFallbackOtrosKind(sp.skillMatchKind) ? (
        <p className={styles.advisory} role="status">
          <span className={cardStyles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
          La coincidencia con un rubro de la plataforma no fue clara; se asignó un valor por defecto. Revisá o
          cambiá el rubro en la lista si corresponde.
        </p>
      ) : null}
      {advisoryNotes.map((text, i) => (
        <p key={i} className={styles.advisory}>
          {text}
        </p>
      ))}
        <section className={cardStyles.projectAutofillRubroDetailBlock}>
        <div className={cardStyles.projectAutofillRubroSelect}>
          <CustomSelect
            labelVariant="projectAutofill"
            value={sp.selectedSkillId ? String(sp.selectedSkillId) : ''}
            onChange={(value) => handleRubroSkillSelect(rid, value, sp)}
            label={
              rubroUnresolvedNeedsPick(sp)
                ? 'Rubro de la plataforma'
                : isRubroFallbackOtrosKind(sp.skillMatchKind)
                  ? 'Rubro (revisar)'
                  : 'Rubro a cotizar'
            }
            outlineInvalid={
              (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ||
              (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId) ||
              isRubroFallbackOtrosKind(sp.skillMatchKind)
            }
            options={skillOptions.map((skill) => ({
              value: String(skill.id),
              label: skill.name
            }))}
            placeholder={
              rubroUnresolvedNeedsPick(sp)
                ? 'Elegí el rubro de la lista'
                : isRubroFallbackOtrosKind(sp.skillMatchKind)
                  ? 'Revisá o cambiá el rubro de la lista'
                  : 'Elegir rubro de la lista'
            }
            searchable={true}
            searchPlaceholder="Buscar rubro..."
            disabled={draftLocked}
            title={draftLocked ? PUBLISHED_HINT : undefined}
          />
        </div>
        <label className={cardStyles.projectAutofillRubroDescriptionField}>
          <span>Descripción</span>
          <textarea
            value={sp.description || ''}
            onChange={(e) => updateRubro(rid, { description: e.target.value })}
            placeholder="Resumen breve del rubro (opcional)…"
            disabled={draftLocked}
            title={draftLocked ? PUBLISHED_HINT : undefined}
          />
        </label>
        {(sp.name || sp.link) && (
          <div className={cardStyles.projectAutofillSubprojectExtras}>
            {sp.name && <span>Nombre: {sp.name}</span>}
            {sp.link && (
              <a
                href={sp.link}
                target="_blank"
                rel="noopener noreferrer"
                className={cardStyles.projectAutofillSubprojectLink}
              >
                Enlace
              </a>
            )}
          </div>
        )}
        {isRubroFuzzyKind(sp.skillMatchKind) ? (
          <label className={cardStyles.projectAutofillFuzzyConfirm}>
            <input
              type="checkbox"
              checked={!!fuzzyConfirmByRubro[rid]}
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_HINT : undefined}
              onChange={(e) =>
                setFuzzyConfirmByRubro((prev) => ({
                  ...prev,
                  [rid]: e.target.checked
                }))
              }
            />
            <span>Confirmo que el rubro elegido es el correcto</span>
          </label>
        ) : null}
        </section>
        <section
          className={`${sectionStyles.section} ${cardStyles.projectAutofillRubroDetailBlock}`}
          aria-labelledby={`rubro-doc-${rid}`}
        >
          <h3 id={`rubro-doc-${rid}`} className={cardStyles.projectAutofillRubroDetailBlockHeading}>
            Documentación para enviar con este rubro
          </h3>
          <RubroDocumentationSectionIntro sourceFileLabel={rubroSourceFileLabel(sp, metadataFiles)} />
          <RubroSheetTabs
            tempId={rid}
            rubro={sp}
            draftId={draftId}
            draftLocked={draftLocked}
            isSaving={isSaving}
            documentationReview={documentationReview}
            updateRubro={updateRubro}
            onOpenMetricSheetFullscreen={onOpenMetricSheetFullscreen}
            onOpenBudgetSheetFullscreen={onOpenBudgetSheetFullscreen}
            onOpenTechnicalSpecFullscreen={onOpenTechnicalSpecFullscreen}
            sourceLineProps={{
              slots,
              fileIds: idsNorm,
              draftFiles: metadataFiles,
              maxPage: docPageCount ?? undefined,
              pdfBaseUrl: docPdfUrl || undefined
            }}
            originHandlers={{
              tempId: rid,
              rubro: sp,
              draftId,
              draftLocked,
              isSaving,
              updateRubro,
              docPdfUrl,
              docPageCount,
              metadataFiles,
              slots,
              fileIds: idsNorm,
              documentationSlotSources: slotSrcNorm,
              uploadingSlotKey,
              onSelectSlotFileId: (slot, fileId) => handleSelectRubroSlotFileId(rid, slot, fileId),
              onSlotPagesChange:
                draftId && !isSaving && !draftLocked
                  ? (slot, pages) => handleRubroSlotPagesChange(rid, slot, pages)
                  : undefined,
              onRemoveSlotPageChip:
                draftId && !isSaving && !draftLocked
                  ? (slot, page, fileId) => handleRubroDocumentationChipRemove(rid, slot, page, fileId)
                  : undefined,
              onUploadSlotFile: (slot, file) => handleUploadRubroSlotFile(rid, slot, file)
            }}
            getMetricBudgetSyncPhase={getMetricBudgetSyncPhase}
            draftAnalyzed={draftAnalyzed}
            onImportMetricEdify={onImportMetricEdify}
            isMetricImporting={isMetricImporting}
            metricImportError={metricImportError}
          />
        </section>
        <RubroQuotationWindowSection
          sectionId={`rubro-quote-window-${rid}`}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          rubroStartDate={sp.startDate || ''}
          rubroEndDate={sp.endDate || ''}
          recommendedQuotationWindow={sp.recommendedQuotationWindow}
          draftLocked={draftLocked}
          today={today}
          disabledTitle={PUBLISHED_HINT}
          clampIsoDateToMax={clampIsoDateToMax}
          onRubroDatesChange={(patch) => updateRubro(rid, patch)}
        />
    </div>
  )
}

export default ProjectAutofillRubroDetailBody
